import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { NotificationBell } from './NotificationBell';
import { toast } from 'sonner';
import { getVapidKey, subscribePush } from '../lib/api';
import { 
  LayoutDashboard, 
  Users, 
  MessageSquare, 
  Megaphone, 
  Calendar, 
  Package, 
  Wallet, 
  ClipboardList, 
  FileText, 
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  UserCheck,
  UsersRound,
  Shield,
  Activity,
  ListTodo,
  Coins,
  Handshake,
  Zap,
  Moon,
  Sun,
  Receipt
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { PWAInstallBanner } from './PWAInstallBanner';
import { SWUpdateBanner } from './SWUpdateBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const menuItems = [
  { path: '/erp/dashboard', label: 'Dashboard', icon: LayoutDashboard, modulo: 'dashboard' },
  { path: '/erp/users', label: 'Usuarios', icon: Users, modulo: 'usuarios' },
  { path: '/erp/groups', label: 'Grupos', icon: UsersRound, modulo: 'grupos' },
  { path: '/erp/messages', label: 'Mensajería', icon: MessageSquare, modulo: 'mensajes' },
  { path: '/erp/announcements', label: 'Anuncios', icon: Megaphone, modulo: 'anuncios' },
  { path: '/erp/calendar', label: 'Calendario', icon: Calendar, modulo: 'eventos' },
  { path: '/erp/attendance', label: 'Asistencia', icon: UserCheck, modulo: 'eventos' },
  { path: '/erp/inventory', label: 'Inventario', icon: Package, modulo: 'inventario' },
  { path: '/erp/finance', label: 'Finanzas', icon: Wallet, modulo: 'finanzas' },
  { path: '/erp/presupuestos', label: 'Presupuestos', icon: Receipt, modulo: 'finanzas' },
  { path: '/erp/cuotas', label: 'Cuotas', icon: Coins, modulo: 'cuotas' },
  { path: '/erp/tasks', label: 'Tareas', icon: ListTodo, modulo: 'tareas' },
  { path: '/erp/surveys', label: 'Encuestas', icon: ClipboardList, modulo: 'encuestas' },
  { path: '/erp/documents', label: 'Documentos', icon: FileText, modulo: 'documentos' },
  { path: '/erp/sponsor', label: 'Patrocinadores', icon: Handshake, modulo: 'patrocinadores' },
  { path: '/erp/automations', label: 'Automatizaciones', icon: Zap, modulo: 'automatizaciones' },
  { path: '/erp/audit', label: 'Auditoría', icon: Activity, modulo: 'auditoria' },
  { path: '/erp/admin', label: 'Administración', icon: Settings, modulo: 'administracion' },
];

export const Layout = ({ children }) => {
  const { user, role, logout, hasPermission, token } = useAuth();
  const { branding } = useNotifications();
  const { theme, toggle: toggleTheme } = useTheme();
  const { connected, lastMessage } = useWebSocket(user?.id, token);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Handle real-time WebSocket messages
  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type === 'new_message') {
      toast.info(`${lastMessage.message?.sender_name}: ${lastMessage.message?.content?.slice(0, 50)}`, {
        description: 'Nuevo mensaje',
        action: { label: 'Ver', onClick: () => navigate('/erp/messages') }
      });
    } else if (lastMessage.type === 'notification') {
      toast.info(lastMessage.title || 'Nueva notificación');
    }
  }, [lastMessage, navigate]);

  // Request push notification permission
  useEffect(() => {
    if (!user || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const setupPush = async () => {
      try {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') return;
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing) return;
        const keyRes = await getVapidKey();
        const pubKey = keyRes.data.public_key;
        if (!pubKey) return;
        const paddedKey = pubKey + '='.repeat((4 - pubKey.length % 4) % 4);
        const rawKey = Uint8Array.from(atob(paddedKey.replace(/-/g,'+').replace(/_/g,'/')), c => c.charCodeAt(0));
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: rawKey });
        const subJson = sub.toJSON();
        await subscribePush({ endpoint: subJson.endpoint, keys: subJson.keys });
      } catch {}
    };
    setupPush();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredMenuItems = menuItems.filter(item => {
    return hasPermission(item.modulo, 'ver');
  });

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const orgName = branding?.organization_name || 'PICHIROCES';
  const tagline = branding?.tagline?.split(' ')[0] || 'Amazónicos';

  return (
    <div className="min-h-screen bg-stone-100 dark:bg-[hsl(160,84%,6%)]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-emerald-950 z-50 flex items-center justify-between px-4">
        <button 
          onClick={() => setSidebarOpen(true)}
          className="text-white p-2"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <span className="text-white font-bold text-lg font-['Outfit']">{orgName}</span>
        <NotificationBell />
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-screen w-64 bg-emerald-950 z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Close button mobile */}
        <button 
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden absolute top-4 right-4 text-white/70 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Logo */}
        <div className="p-6 border-b border-emerald-800">
          <h1 className="text-xl font-bold text-white font-['Outfit'] tracking-tight">
            {orgName}
          </h1>
          <p className="text-emerald-300 text-xs mt-1 tracking-wider uppercase">
            {tagline}
          </p>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-200px)]">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path.slice(1)}`}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg
                  transition-colors duration-200
                  ${isActive 
                    ? 'bg-emerald-800 text-white font-medium' 
                    : 'text-emerald-200 hover:bg-emerald-900 hover:text-white'
                  }
                `}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-emerald-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-emerald-900 transition-colors"
                data-testid="user-menu-btn"
              >
                <Avatar className="w-10 h-10 border-2 border-emerald-700">
                  <AvatarImage src={user?.avatar_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : user?.avatar_url} />
                  <AvatarFallback className="bg-emerald-700 text-white text-sm">
                    {getInitials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-white text-sm font-medium truncate">
                    {user?.full_name}
                  </p>
                  <p className="text-emerald-400 text-xs truncate">
                    {role?.nombre || role?.name || 'Usuario'}
                  </p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/erp/dashboard')}>
                Mi Perfil
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Desktop Header with Notifications */}
        <div className="hidden lg:flex items-center justify-end gap-3 p-4 border-b bg-white dark:bg-emerald-950 dark:border-emerald-800">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-emerald-900 transition-colors"
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-stone-500" />}
          </button>
          <NotificationBell />
        </div>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
      <PWAInstallBanner />
      <SWUpdateBanner />
    </div>
  );
};
